import { Filter, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SUBMISSION_TYPE_OPTIONS } from "@/features/submissions/lib/submission-display";
import type { Department } from "@/types/department";
import type { SubmissionType } from "@/types/submission";

interface SubmissionsFiltersProps {
  search: string;
  type?: SubmissionType;
  departmentId?: string;
  hasAttachment: boolean;
  departments: Department[];
  onSearchChange: (value: string) => void;
  onTypeChange: (value: SubmissionType | undefined) => void;
  onDepartmentChange: (value: string | undefined) => void;
  onHasAttachmentChange: (value: boolean) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export function SubmissionsFilters({
  search,
  type,
  departmentId,
  hasAttachment,
  departments,
  onSearchChange,
  onTypeChange,
  onDepartmentChange,
  onHasAttachmentChange,
  onClearFilters,
  hasActiveFilters,
}: SubmissionsFiltersProps) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="size-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">تصفية الطلبات</h3>
        </div>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            <X className="ml-1 size-4" />
            مسح الفلاتر
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-56 flex-col gap-1 text-xs text-muted-foreground">
          بحث
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="الاسم، البريد، الموضوع أو الرسالة"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
          />
        </label>

        <div className="flex min-w-44 flex-col gap-1">
          <span className="text-xs text-muted-foreground">النوع</span>
          <Select
            value={type ?? "all"}
            onValueChange={(v) =>
              onTypeChange(v === "all" ? undefined : (v as SubmissionType))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="كل الأنواع" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الأنواع</SelectItem>
              {SUBMISSION_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-44 flex-col gap-1">
          <span className="text-xs text-muted-foreground">الدائرة</span>
          <Select
            value={departmentId ?? "all"}
            onValueChange={(v) => onDepartmentChange(v === "all" ? undefined : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="كل الدوائر" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الدوائر</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.nameAr ?? d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex h-9 items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={hasAttachment}
            onChange={(e) => onHasAttachmentChange(e.target.checked)}
            className="size-4 accent-primary"
          />
          مع مرفق فقط
        </label>
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {search && <Badge variant="secondary">بحث: {search}</Badge>}
          {type && (
            <Badge variant="secondary">
              {SUBMISSION_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type}
            </Badge>
          )}
          {departmentId && (
            <Badge variant="secondary">
              {departments.find((d) => d.id === departmentId)?.nameAr ??
                departmentId}
            </Badge>
          )}
          {hasAttachment && <Badge variant="secondary">مع مرفق</Badge>}
        </div>
      )}
    </div>
  );
}
